import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, type ReactNode } from 'react'

import type { Permission } from '@aeris/permissions'

import {
  ApiUnreachableError,
  fetchSession,
  login as loginRequest,
  logout as logoutRequest,
  exitSupport as exitSupportRequest,
  switchContext as switchContextRequest,
  UnauthenticatedError,
} from './api.ts'
import { AuthContext, type AuthState } from './auth-context.ts'
import {
  forgetSession,
  recallSession,
  rememberSession,
} from './offline-session.ts'
import { rememberAccount } from './saved-accounts.ts'

/**
 * Session state, owned by the server.
 *
 * This replaces `lib/session.ts`, which F002 shipped as a placeholder and whose
 * own header promised it would be deleted rather than extended. Nothing here
 * decides whether a user is signed in: it asks the API, and the API decides.
 *
 * `allows()` exists to hide what a user cannot do. It is never the enforcement
 * — the server checks the same permission independently on every request, and
 * `@aeris/permissions` is the one package both sides share so the two answers
 * cannot drift. Master Specification section 28.
 */

const SESSION_KEY = ['session'] as const

export const AuthProvider = ({
  children,
}: {
  children: ReactNode
}): ReactNode => {
  const queryClient = useQueryClient()

  const { data, error, isLoading } = useQuery({
    queryKey: SESSION_KEY,
    queryFn: async ({ signal }) => {
      try {
        const next = await fetchSession(signal)
        rememberSession(next)
        return next
      } catch (failure) {
        // A 401 is the server saying "not you". That answer outranks anything
        // this machine remembers, so the cache goes with it.
        if (failure instanceof UnauthenticatedError) forgetSession()
        throw failure
      }
    },
    /**
     * A 401 is the answer, not a failure to retry.
     *
     * Retrying it would delay the login screen for every anonymous visitor and
     * put three pointless requests on a cold start.
     */
    retry: (failureCount, error) =>
      !(error instanceof UnauthenticatedError) && failureCount < 1,
    staleTime: 60_000,

    /**
     * Keep knocking while the API is unreachable, and only then.
     *
     * The `online` event below is the fast path, but it cannot be the only one:
     * it fires for the network interface, not for the server, so it misses a
     * connection that never technically dropped — a cable that was fine while
     * the API was restarting, a Wi-Fi that stayed associated to a router with
     * no uplink. Those are the ordinary outages in a shop.
     *
     * The interval exists only in the failed state, so a healthy till makes one
     * session request and then none: this is not a poll, it is a retry that
     * gives up as soon as it succeeds.
     */
    refetchInterval: (query) =>
      query.state.error instanceof ApiUnreachableError ? 10_000 : false,
    refetchIntervalInBackground: false,
  })

  /**
   * Fall back to the remembered session only when the API could not be
   * reached — never when it answered.
   *
   * The distinction is the whole safety of this: an unreachable API told us
   * nothing, so yesterday's answer is the best available; a 401 told us
   * something, and surviving it with a cache would be the interface overruling
   * the server. `ApiUnreachableError` is the only error that means the former.
   */
  const isUnreachable = error instanceof ApiUnreachableError
  // Memoised because `recallSession` parses and returns a fresh object: reading
  // it on every render would give every consumer a new session identity and
  // defeat the memos below.
  const remembered = useMemo(
    () => (isUnreachable ? recallSession() : null),
    [isUnreachable]
  )
  const session = data ?? remembered
  /** The identity on screen came from this machine, not from the server. */
  const isUnverified = data === undefined && remembered !== null

  /**
   * Ask again the moment the machine says it has a network.
   *
   * Without this the recovery is worse than the outage: the session query has
   * already spent its one retry, nothing refetches it, and the operator sits in
   * front of a till that says "sem conexão" on a connection that came back
   * minutes ago. The only way out would be F5 — and knowing to press F5 is not
   * something a shop assistant should have to know.
   *
   * `online` is a hint about the network interface, not proof the API is
   * reachable, which is exactly why it triggers a refetch rather than a change
   * of state: the request is what decides.
   */
  useEffect(() => {
    const retry = (): void => {
      void queryClient.refetchQueries({ queryKey: SESSION_KEY })
    }
    window.addEventListener('online', retry)
    return () => {
      window.removeEventListener('online', retry)
    }
  }, [queryClient])

  const signIn = useCallback(
    async (input: { email: string; password: string }) => {
      const next = await loginRequest(input)
      /*
       * A conta entra na lista deste aparelho DEPOIS de o servidor aceitar
       * (F151, ampliado na F153).
       *
       * Antes da chamada, um endereço digitado errado — ou o de quem errou a
       * senha e desistiu — viraria uma das caras da tela de amanhã.
       *
       * O NOME vem da resposta, não do que foi digitado: é a única fonte que
       * sabe como a pessoa se chama, e é ele que transforma uma lista de
       * endereços numa lista de gente.
       */
      rememberAccount({
        email: next.user.email,
        displayName: next.user.displayName,
      })
      rememberSession(next)
      queryClient.setQueryData(SESSION_KEY, next)
    },
    [queryClient]
  )

  const signOut = useCallback(async () => {
    try {
      await logoutRequest()
    } finally {
      // The local state is cleared whatever the server said. A network failure
      // must not leave someone looking signed in on a shared terminal.
      forgetSession()
      /*
       * A lista de contas SOBREVIVE ao logout, de propósito (F151/F153) — é o
       * pedido inteiro: "se deslogar, salvar o e-mail pelo menos, pra só botar
       * a senha". Quem quiser tirar uma conta usa "Remover deste computador"
       * na tela de login.
       *
       * Não é esquecimento: é a linha que não deve ser escrita aqui.
       */
      queryClient.setQueryData(SESSION_KEY, null)
      await queryClient.invalidateQueries()
    }
  }, [queryClient])

  /**
   * Sai do acesso de suporte (F169).
   *
   * Invalida TUDO menos a sessão, como a troca de contexto: o que estava na
   * tela pertencia à loja do cliente, e continuar mostrando aqueles dados
   * depois de voltar seria a pior forma de errar — números de outra empresa
   * embaixo do nome da sua.
   */
  const exitSupport = useCallback(async () => {
    const next = await exitSupportRequest()
    rememberSession(next)
    queryClient.setQueryData(SESSION_KEY, next)
    await queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] !== 'session',
    })
  }, [queryClient])

  const switchContext = useCallback(
    async (input: {
      tenantId: string
      companyId: string | null
      branchId: string | null
    }) => {
      const next = await switchContextRequest(input)
      rememberSession(next)
      queryClient.setQueryData(SESSION_KEY, next)
      // Everything else on screen was scoped to the previous context.
      await queryClient.invalidateQueries({
        predicate: (query) => query.queryKey[0] !== 'session',
      })
    },
    [queryClient]
  )

  /**
   * O que esta pessoa pode, segundo a própria sessão.
   *
   * Era `can(role, permission)` — o papel cru —, e por isso a tela do admin que
   * escolhe as telas de cada conta (F038) não mudava nada no navegador: o
   * servidor descontava as telas negadas e mandava o resultado em
   * `permissions`, e o menu jogava fora essa resposta para recalcular a partir
   * do papel. O caixa da primeira loja entrou e viu o menu inteiro.
   *
   * `can` continua existindo para quem tem um papel na mão e nenhuma sessão —
   * o servidor, a tela de equipe — e é por isso que o pacote compartilhado
   * segue sendo a fonte única: o que mudou é quem responde aqui.
   */
  const allows = useCallback(
    (permission: Permission) =>
      session?.permissions.includes(permission) ?? false,
    [session]
  )

  /**
   * Se esta tela aparece para esta pessoa.
   *
   * Pergunta diferente de `allows`, e o produto tem três lugares onde as duas
   * discordam: Vendas e Orçamentos exigem `sales.read`, Produtos e Aplicações
   * exigem `catalog.read`, Caixa e Terminais exigem `pos.operate`. Tirar uma
   * de cada par não pode revogar a permissão que a outra usa — então a
   * permissão fica, e só esta lista sabe que a tela saiu.
   *
   * Sessão sem `pages` é sessão guardada por uma versão anterior do produto:
   * volta a valer só a permissão, que é o comportamento de antes desta
   * correção, e a próxima resposta do servidor conserta.
   */
  const showsPage = useCallback(
    (module: string) =>
      session === null ||
      session.pages.length === 0 ||
      session.pages.includes(module),
    [session]
  )

  /**
   * A chave desta conta (F169).
   *
   * Sessão nula responde `false`, ao contrário de `showsPage`: chave é
   * CONCESSÃO, e o padrão de uma concessão desconhecida é não conceder. Foi o
   * inverso da escolha da F038, onde a ausência de informação devolve a tela.
   */
  const hasFeature = useCallback(
    (feature: string) => session?.features.includes(feature) ?? false,
    [session]
  )

  const value = useMemo<AuthState>(
    () => ({
      session,
      isLoading,
      isAuthenticated: session !== null,
      isUnverified,
      allows,
      showsPage,
      hasFeature,
      isPlatformAdmin: session?.user.isPlatformAdmin ?? false,
      support: session?.support ?? null,
      signIn,
      signOut,
      exitSupport,
      switchContext,
    }),
    [
      session,
      isLoading,
      isUnverified,
      allows,
      showsPage,
      hasFeature,
      signIn,
      signOut,
      exitSupport,
      switchContext,
    ]
  )

  return <AuthContext value={value}>{children}</AuthContext>
}
